import React, { useRef, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

/**
 * overflow-x-auto sarmalayıcısı için viewport altına sabitlenen senkron yatay kaydırma çubuğu.
 * Sayfa düzenini değiştirmez; yalnızca grid yatay kaydırmasını erişilebilir kılar.
 */
const FixedHorizontalScroll = ({ children, className = '' }) => {
  const scrollRef = useRef(null);
  const barRef = useRef(null);
  const isSyncingRef = useRef(false);
  const [barState, setBarState] = useState({
    visible: false,
    scrollWidth: 0,
    left: 0,
    width: 0,
  });

  const updateBarState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const hasOverflow = el.scrollWidth > el.clientWidth + 1;
    const inView = rect.bottom > 0 && rect.top < window.innerHeight;

    setBarState({
      visible: hasOverflow && inView,
      scrollWidth: el.scrollWidth,
      left: Math.max(rect.left, 0),
      width: rect.width,
    });
  }, []);

  useEffect(() => {
    updateBarState();
    const el = scrollRef.current;
    if (!el) return undefined;

    const observer = new ResizeObserver(updateBarState);
    observer.observe(el);
    const tableEl = el.querySelector('table');
    if (tableEl) observer.observe(tableEl);

    window.addEventListener('scroll', updateBarState, true);
    window.addEventListener('resize', updateBarState);

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', updateBarState, true);
      window.removeEventListener('resize', updateBarState);
    };
  }, [updateBarState]);

  const syncToBar = useCallback(() => {
    if (isSyncingRef.current) return;
    isSyncingRef.current = true;
    if (scrollRef.current && barRef.current) {
      barRef.current.scrollLeft = scrollRef.current.scrollLeft;
    }
    requestAnimationFrame(() => {
      isSyncingRef.current = false;
    });
  }, []);

  const syncToTable = useCallback(() => {
    if (isSyncingRef.current) return;
    isSyncingRef.current = true;
    if (scrollRef.current && barRef.current) {
      scrollRef.current.scrollLeft = barRef.current.scrollLeft;
    }
    requestAnimationFrame(() => {
      isSyncingRef.current = false;
    });
  }, []);

  return (
    <>
      <div ref={scrollRef} className={className} onScroll={syncToBar}>
        {children}
      </div>
      {barState.visible &&
        createPortal(
          <div
            ref={barRef}
            onScroll={syncToTable}
            aria-label="Yatay kaydırma"
            className="fixed z-40 overflow-x-auto overflow-y-hidden h-4 border-t border-gray-300 dark:border-gray-600 bg-gray-100/95 dark:bg-gray-900/95 backdrop-blur-sm shadow-[0_-2px_8px_rgba(0,0,0,0.08)] fixed-h-scroll-bar"
            style={{ bottom: 0, left: barState.left, width: barState.width }}
          >
            <div style={{ width: barState.scrollWidth, height: 1 }} aria-hidden="true" />
          </div>,
          document.body
        )}
    </>
  );
};

export default FixedHorizontalScroll;
